import {
  type FetchOrderPricingResponse,
  type OrderPricingParams,
  PricingEndpoint,
} from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { computeOrderSubtotalForSplitPayment } from '~shared/features/pricing/orderPricingSplitPayment';
import { type ItemPricing } from '~shared/features/pricing/types';
import { apiEndpointHandler } from '~shared/helpers/api';
import { get } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { itemPricingMap } from '~shared/reducers/pricing';

const PricingApi = PricingEndpoint(apiEndpointHandler);

const getTotal = (data: FetchOrderPricingResponse) => {
  return data.line_items?.find(({ key }) => key === 'total');
};

const isPriceUnconfirmed = (data: FetchOrderPricingResponse) => {
  const orderTotal = getTotal(data);
  if (!orderTotal) {
    return true;
  }

  return orderTotal.info_key === 'price_unavailable' || orderTotal.info_key === 'estimated';
};

export const pricing = createQueryKeys('pricing', {
  order: ({
    shipmentID,
    order,
    bundlingOptionShipmentID,
  }: {
    shipmentID?: number;
    order?: OrderPricingParams;
    bundlingOptionShipmentID?: number;
  }) => ({
    queryKey: [{ shipmentID, order, bundlingOptionShipmentID }],
    queryFn: async () => {
      const response = await PricingApi.fetchOrderPricing(
        { order, shipment_id: shipmentID, bundling_option_shipment_id: bundlingOptionShipmentID },
        { version: 'v6' },
      );

      if (response.errors?.length) {
        throw new Error(response.errors[0].title || 'Error fetching shipment pricing');
      }

      return {
        has_copay: isPriceUnconfirmed(response) || !!computeOrderSubtotalForSplitPayment(response), // med cost + program dispensing fee minus shipment fee or tip
        item_pricing: itemPricingMap(response.item_pricing),
        line_items: response.line_items,
        metadata: response.metadata,
        price_unconfirmed: isPriceUnconfirmed(response),
        subtotal: computeOrderSubtotalForSplitPayment(response),
        total: getTotal(response),
      };
    },
  }),
  prescriptionById: ({ prescriptionId }: { prescriptionId: number }) => ({
    queryKey: [{ prescriptionId }],
    queryFn: async () => {
      const response = await PricingApi.fetchPrescriptionPricingById({ id: prescriptionId }, { version: 'v4' });

      return {
        item_pricing: response.item_pricing,
        errors: response.errors,
      };
    },
  }),
  // Bulk pricing for all of the patient's prescriptions, keyed by prescription_id.
  // Mirrors the deprecated fetchPrescriptionPricing thunk; bridged into Redux via
  // usePrescriptionPricingReduxBridge so the pricing selectors keep working.
  prescriptions: {
    queryKey: null,
    queryFn: async (): Promise<ItemPricing> => {
      const response = await get('/pricing/prescriptions', {}, { version: 'v4' });
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return itemPricingMap(response.item_pricing);
    },
  },
});
